// api.service.js — Polyp API helper
// Works with API Gateway base: REACT_APP_API_BASE_URL
// Auth: Bearer token from AuthService.getAccessToken()

import AuthService from "./auth.service";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_FIREBASE_API_KEY;

/** Compose headers with optional JWT + API key */
async function authHeaders(extra = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(extra || {}),
  };

  // Attach Firebase access token if available
  try {
    if (AuthService?.getAccessToken) {
      const token = await AuthService.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) {
    // token retrieval failed — request will proceed without auth
  }

  // Optional API key support
  if (API_KEY) {
    headers["x-api-key"] = API_KEY;
  }

  return headers;
}

/** Fetch JSON with consistent errors + better CORS visibility (AUTHENTICATED) */
async function fetchJSON(endpoint, options = {}) {
  if (!API_BASE) {
    throw new Error("Missing REACT_APP_API_BASE_URL environment variable");
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  const finalOptions = {
    ...options,
    headers: options.headers || (await authHeaders(options.headers)),
  };

  let res;
  try {
    res = await fetch(url, finalOptions);
  } catch (err) {
    throw new Error(`API call failed: ${err.message}`);
  }

  if (res.status === 204) return {};

  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let code = null;
    let detail = null;
    let existingAccountType = null;
    try {
      const j = await res.json();
      if (j?.message) msg = `${msg} — ${j.message}`;
      else if (j?.error) { msg = `${msg} — ${j.error}`; code = j.error; }
      detail = j?.detail ?? null;
      existingAccountType = j?.existingAccountType ?? null;
    } catch (_) {}
    const err = new Error(msg);
    // Additive: a caller that needs to TELL ERRORS APART (e.g. a 409
    // gameCompleted, which is a decision to offer, not a failure to report)
    // should branch on these rather than regex the message string.
    err.status = res.status;
    err.code = code;
    // `detail` is the tag validator's human-readable reason ("At least 3
    // characters", "Letters, numbers, and _ only") — the code alone can't say
    // WHICH rule a hashtag broke, and re-deriving it client-side would mean a
    // fourth copy of the server's frozen tagCheck. `existingAccountType` is
    // the same idea for 409 phoneHasIncompatibleAccount.
    err.detail = detail;
    err.existingAccountType = existingAccountType;
    throw err;
  }

  return await res.json();
}

/**
 * PUBLIC fetch (NO AUTH TOKEN)
 * Used for routes like /public/registration-links/{slug}
 * Keeps x-api-key if you use it; removes Authorization entirely.
 */
async function publicFetchJSON(endpoint, options = {}) {
  if (!API_BASE) {
    throw new Error("Missing REACT_APP_API_BASE_URL environment variable");
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (API_KEY && !headers["x-api-key"]) {
    headers["x-api-key"] = API_KEY;
  }

  // Ensure NO Authorization header on public calls
  if ("Authorization" in headers) delete headers.Authorization;
  if ("authorization" in headers) delete headers.authorization;

  const finalOptions = {
    ...options,
    headers,
  };

  let res;
  try {
    res = await fetch(url, finalOptions);
  } catch (err) {
    throw new Error(`Public API call failed: ${err.message}`);
  }

  if (res.status === 204) return {};

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  let body = null;
  try {
    body = isJson ? await res.json() : await res.text();
  } catch {
    body = null;
  }

  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    if (body && typeof body === "object") {
      if (body?.message) msg = `${msg} — ${body.message}`;
      else if (body?.error) msg = `${msg} — ${body.error}`;
    } else if (typeof body === "string" && body.trim()) {
      msg = `${msg} — ${body.trim()}`;
    }
    throw new Error(msg);
  }

  return body;
}

// ===============================================
// API ENDPOINT IMPLEMENTATIONS (Wrappers for fetchJSON)
// ===============================================

// --- User/Business Authentication ---
const getMe = () => fetchJSON("/me");
const getMeProfile = () => fetchJSON("/me");
const getMeBusiness = () => fetchJSON("/me/business");
const getMySubscriptions = () => fetchJSON("/me/subscriptions");

// IMPORTANT: send ?accessLevel=everything by default
const getAuthorizedBusinesses = (accessLevel = "everything") =>
  fetchJSON(`/me/authorized-businesses?accessLevel=${encodeURIComponent(accessLevel)}`);

// --- PUBLIC: Registration Link Lookup (no auth) ---
const getPublicRegistrationLink = (slug) => {
  if (!slug) throw new Error("Missing slug");
  return publicFetchJSON(`/public/registration-links/${encodeURIComponent(slug)}`);
};

// --- Groups ---
const getGroups = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/groups`);

const getGroupMembers = (groupId) =>
  fetchJSON(`/groups/${encodeURIComponent(groupId)}/members`);

const createGroup = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

const addClientsToGroup = (businessId, groupId, clientIds) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}/members`,
    { method: "POST", body: JSON.stringify({ clientIds }) }
  );

const updateGroup = (businessId, groupId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}`,
    { method: "PUT", body: JSON.stringify(payload) }
  );

const deleteGroup = (businessId, groupId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}`,
    { method: "DELETE" }
  );

const removeClientsFromGroup = (businessId, groupId, clientIds) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}/members`,
    { method: "DELETE", body: JSON.stringify({ clientIds }) }
  );

// --- Clients ---
const getClients = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients`);

const addClient = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// Manual add — used by the "Add as lead" path. Hits the legacy
// createBusinessClient Lambda which writes a BusinessToClients row with the
// caller-supplied status ("lead" → no SMS, no Stripe, no subscription).
const addManualClient = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// New Stripe-only add-client paths (web CRM, business-initiated).
// (a) Invite link: creates a PendingClients row + sends SMS invite. Returns inviteUrl.
const createPendingClient = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients/pending`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// (b) Collect payment now: creates PendingClients + Stripe customer + subscription
// using the paymentMethodId collected client-side via Stripe Elements.
const collectClientPayment = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients/collect-payment`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// (c) Client-initiated deferred-charge subscribe. Posts the platform PaymentMethod
// + SetupIntent + plan info; backend records a PendingClients row tagged
// source:"client-self-signup". Card is NOT charged until the business owner
// taps Accept in their CRM (acceptSelfRequest below).
const requestSubscription = (payload) =>
  fetchJSON("/me/subscriptions/request", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const acceptSelfRequest = (businessId, personId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/clients/self-request/${encodeURIComponent(personId)}/accept`,
    { method: "POST", body: "{}" }
  );

const denySelfRequest = (businessId, personId, reason) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/clients/self-request/${encodeURIComponent(personId)}/deny`,
    { method: "POST", body: JSON.stringify({ reason: reason || "" }) }
  );

// Mints a SetupIntent on the user's platform Stripe customer. Returns
// {clientSecret, customerId, setupIntentId, ephemeralKey, publishableKey}.
// Used by SubscribeModal so the client can save a card before any business
// charges it.
const parentSetupIntent = (payload = {}) =>
  fetchJSON("/payments/parent-setup-intent", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const lookupClientByPhone = async (businessId, phone) => {
  try {
    return await fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients/lookup?phone=${encodeURIComponent(phone)}`);
  } catch {
    return null; // Endpoint may not exist yet — fail silently
  }
};

// --- Matchmaking ---
// Owner-facing: submit / list / withdraw a business's match requests.
const createMatchRequest = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/match-requests`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

const getMyMatchRequests = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/match-requests`);

const cancelMatchRequest = (businessId, requestId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/match-requests/${encodeURIComponent(requestId)}/cancel`,
    { method: "POST", body: "{}" }
  );

// Owner-facing match history: all matches the business is a side of (caller-oriented
// ourScore/theirScore/result), plus the per-player box score for one match.
const getBusinessMatches = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/matches`);

const getBusinessMatchStats = (businessId, matchId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/matches/${encodeURIComponent(matchId)}/stats`
  );

// Platform-admin: review all requests + pair two academies.
const getAdminMatchRequests = (status = "pending") =>
  fetchJSON(`/admin/match-requests?status=${encodeURIComponent(status)}`);

const createMatch = (payload) =>
  fetchJSON("/admin/matches", { method: "POST", body: JSON.stringify(payload) });

// Platform-admin: post-match per-player stats.
const getAdminMatches = () => fetchJSON("/admin/matches");

const getAdminMatchStats = (matchId) =>
  fetchJSON(`/admin/matches/${encodeURIComponent(matchId)}/stats`);

const saveAdminMatchStats = (matchId, payload) =>
  fetchJSON(`/admin/matches/${encodeURIComponent(matchId)}/stats`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// Platform-admin: Instagram lead-ad parents awaiting placement into an academy.
const getAdminPlacements = (status = "awaiting_placement") =>
  fetchJSON(`/admin/placements?status=${encodeURIComponent(status)}`);

const placeAdminLead = (userId, businessId) =>
  fetchJSON(`/admin/placements/${encodeURIComponent(userId)}/place`, {
    method: "POST",
    body: JSON.stringify(businessId ? { businessId } : {}),
  });

// Official-facing: league-assigned games + stat entry. A league admin
// assigns an official directly to a LeagueGames row now — there is no more
// apply/cancel (removed 2026-07-30, see ios/Lambda/polyp-official-games's
// header) or "available" scope; every game returned is already the
// caller's. Server-gated to accountType === "official" (403 officialOnly)
// with officialStatus === "approved" (403 officialNotApproved), OR
// accountType === "league" — a league account has been admitted since
// 2026-08-03 (self-assigned scorekeeping) and since 2026-09-15 reaches the
// per-game stats routes for ANY game in its own league, whoever is assigned.
// Backs both OfficialDashboard and the league console's schedule page.
const getOfficialGames = (scope = "upcoming") =>
  fetchJSON(`/official/games?scope=${encodeURIComponent(scope)}`);

const getOfficialMatchStats = (matchId) =>
  fetchJSON(`/official/games/${encodeURIComponent(matchId)}/stats`);

const saveOfficialMatchStats = (matchId, payload) =>
  fetchJSON(`/official/games/${encodeURIComponent(matchId)}/stats`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// Coach-facing: roster CRUD + read-only games (Players/Teams/Games tabs on
// web — scorekeeping is iOS-only, and league-scheduled now, not
// coach-owned). Server-gated to accountType === "coach" (403 coachOnly).
//
// getCoachTeam() -> getCoachTeams() (2026-08-03, multi-team support): a
// coach can be linked to more than one LeagueTeams row, so the singular
// GET /coach/team route was retired in favor of GET /coach/teams, which
// always returns the full list ([] before any league admin has assigned
// one). getCoachPlayers/createCoachPlayer now take an optional teamId —
// required once the coach has 2+ teams (the server 400s teamIdRequired if
// omitted), optional and defaulting to the sole team otherwise.
const getCoachTeams = () => fetchJSON("/coach/teams");

// { name, tag } — a coach creates their OWN independent team. Unlike
// createLeagueTeam, `tag` is REQUIRED and globally unique (400 tagRequired /
// invalidTag, 409 tagTaken): the hashtag is how a league finds and invites
// the team, and a team with none is unreachable.
//
// A 201 carrying `joinedLeagueId` means the server also auto-joined a league:
// this coach claimed a teamless SMS invite, the claim stamped
// Users.invitedByLeagueId, and creating a team turned that into an ACTIVE
// affiliation — no separate accept, because claiming the invite WAS the
// consent. The league id is read off the Users row, never off this body, so a
// coach cannot join a league that did not invite them.
const createCoachTeam = (payload) =>
  fetchJSON("/coach/teams", {
    method: "POST",
    body: JSON.stringify(payload),
  });

// The coach's half of the league-invitation handshake. KEYED ON leagueId, not
// teamId: one call answers for EVERY pending row this coach has in that
// league and returns the teamIds it touched, so a coach with two teams invited
// by one league answers once.
const getCoachAffiliations = () => fetchJSON("/coach/affiliations");

// 200 { accepted, teamIds } — or { alreadyAccepted: true } on a replay, which
// is a success, not an error. 404 inviteNotFound = cancelled, or already
// answered elsewhere.
const acceptCoachAffiliation = (leagueId) =>
  fetchJSON(`/coach/affiliations/${encodeURIComponent(leagueId)}`, { method: "PUT" });

// DELETES the row rather than tombstoning it, so the league can invite again
// later. Any UI must say so, or "Decline" reads as permanent.
const declineCoachAffiliation = (leagueId) =>
  fetchJSON(`/coach/affiliations/${encodeURIComponent(leagueId)}`, { method: "DELETE" });

// Team logo (2026-08) — presign mirrors getBusinessImageUploadUrl's shape
// ({url, key, imageUrl}); the caller PUTs the file to `url`, then calls
// updateCoachTeam with the resulting imageUrl. teamId must be one of the
// coach's own (403 notYourTeam otherwise).
const getCoachTeamLogoUploadUrl = (teamId) =>
  fetchJSON(`/coach/teams/${encodeURIComponent(teamId)}/logo`, { method: "POST" });

const updateCoachTeam = (teamId, payload) =>
  fetchJSON(`/coach/teams/${encodeURIComponent(teamId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const getCoachPlayers = (teamId) =>
  fetchJSON(teamId ? `/coach/players?teamId=${encodeURIComponent(teamId)}` : "/coach/players");

const createCoachPlayer = (teamId, payload) =>
  fetchJSON(teamId ? `/coach/players?teamId=${encodeURIComponent(teamId)}` : "/coach/players", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const updateCoachPlayer = (playerId, payload) =>
  fetchJSON(`/coach/players/${encodeURIComponent(playerId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const archiveCoachPlayer = (playerId) =>
  fetchJSON(`/coach/players/${encodeURIComponent(playerId)}`, {
    method: "DELETE",
  });

const getCoachGames = (scope = "upcoming") =>
  fetchJSON(`/coach/games?scope=${encodeURIComponent(scope)}`);

const getCoachGameStats = (gameId) =>
  fetchJSON(`/coach/games/${encodeURIComponent(gameId)}/stats`);

const getCoachPlayerStats = (playerId) =>
  fetchJSON(`/coach/players/${encodeURIComponent(playerId)}/stats`);

// League-facing: create teams, schedule games, assign officials. Coach and
// official logins are no longer provisioned with a league-supplied password
// (2026-08 "SMS invites" rewrite) — see createLeagueInvite/getLeagueInvites/
// resendLeagueInvite/revokeLeagueInvite below, and lookupStaffInvite/
// claimStaffInvite for the public claim-link flow. Server-gated to
// accountType === "league" (403 leagueOnly).
const getLeague = () => fetchJSON("/league");

const putLeague = (name, sport) =>
  fetchJSON("/league", {
    method: "PUT",
    body: JSON.stringify({ name, sport }),
  });

const getLeagueTeams = () => fetchJSON("/league/teams");

const createLeagueTeam = (payload) =>
  fetchJSON("/league/teams", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const updateLeagueTeam = (teamId, payload) =>
  fetchJSON(`/league/teams/${encodeURIComponent(teamId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const deleteLeagueTeam = (teamId) =>
  fetchJSON(`/league/teams/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
  });

// Team logo (2026-08) — same presign shape as getCoachTeamLogoUploadUrl;
// the caller PUTs the file to `url`, then calls updateLeagueTeam with
// { logoUrl: imageUrl } (updateLeagueTeam already accepts either field —
// {name?, logoUrl?} — no separate persist call needed). A league admin can
// set this on any team in their own league, including one whose coach
// hasn't claimed their invite yet.
const getLeagueTeamLogoUploadUrl = (teamId) =>
  fetchJSON(`/league/teams/${encodeURIComponent(teamId)}/logo`, { method: "POST" });

const getLeagueTeamPlayers = (teamId) =>
  fetchJSON(`/league/teams/${encodeURIComponent(teamId)}/players`);

// Roster CRUD for a league admin (2026-08-22). Until these existed the league
// side could only READ a roster — /coach/players is gated coachOnly, so an
// admin whose coach hadn't claimed their invite had no way to add a player.
//
// Unlike the coach trio above, teamId rides on ALL THREE verbs: the league
// Lambda scopes every write by (leagueId, teamId), which is what stops an
// admin reaching another league's roster. DELETE is a soft archive server-side.
const createLeagueTeamPlayer = (teamId, payload) =>
  fetchJSON(`/league/teams/${encodeURIComponent(teamId)}/players`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

const updateLeagueTeamPlayer = (teamId, playerId, payload) =>
  fetchJSON(
    `/league/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
    { method: "PUT", body: JSON.stringify(payload) }
  );

const archiveLeagueTeamPlayer = (teamId, playerId) =>
  fetchJSON(
    `/league/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
    { method: "DELETE" }
  );

const getLeagueGames = (scope = "upcoming") =>
  fetchJSON(`/league/games?scope=${encodeURIComponent(scope)}`);

const createLeagueGame = (payload) =>
  fetchJSON("/league/games", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const updateLeagueGame = (gameId, payload) =>
  fetchJSON(`/league/games/${encodeURIComponent(gameId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const deleteLeagueGame = (gameId) =>
  fetchJSON(`/league/games/${encodeURIComponent(gameId)}`, {
    method: "DELETE",
  });

const getLeagueOfficials = () => fetchJSON("/league/officials");

const removeLeagueOfficial = (officialId) =>
  fetchJSON(`/league/officials/${encodeURIComponent(officialId)}`, {
    method: "DELETE",
  });

// SMS invites — mint/list/resend/revoke a StaffInvites row. `payload` is
// { accountType: "coach"|"official", phoneNumber, firstName?, lastName?, teamId? }
// (teamId only meaningful for accountType "coach"). Response includes
// { inviteUrl, smsSent, smsReason } — smsSent:false (e.g. a non-US phone)
// still returns a usable inviteUrl to copy/share manually.
const createLeagueInvite = (payload) =>
  fetchJSON("/league/invites", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const getLeagueInvites = (accountType) =>
  fetchJSON(`/league/invites${accountType ? `?accountType=${encodeURIComponent(accountType)}` : ""}`);

const resendLeagueInvite = (inviteId) =>
  fetchJSON(`/league/invites/${encodeURIComponent(inviteId)}/resend`, {
    method: "POST",
  });

const revokeLeagueInvite = (inviteId) =>
  fetchJSON(`/league/invites/${encodeURIComponent(inviteId)}`, {
    method: "DELETE",
  });

// Affiliations — coach-owned teams that belong to (or have been INVITED to)
// this league. A team here lives in its OWN LeagueTeams partition, not this
// league's, which is why every league write against one 403s coachOwnedTeam.
//
// Since 2026-09-16 createLeagueAffiliation is an INVITATION, not an add: it
// writes status "pending" and notifies the coach, who answers on
// PUT/DELETE /coach/affiliations/{leagueId}. Every affiliation reader
// server-side ALLOWLISTS "active", so a pending team is invisible in
// GET /league/teams, scheduling, officials and on playerwiki.org until then.
// getLeagueAffiliations is the one place pending rows can be seen at all.
const getLeagueAffiliations = () => fetchJSON("/league/affiliations");

// { tag } — the target team's public #hashtag. 404 tagNotFound · 409
// alreadyInLeague / alreadyAffiliated / invitePending (NOT a failure — the
// server re-notified the coach) / teamHasNoCoach (nobody can consent, so use
// the SMS invite path instead).
const createLeagueAffiliation = (tag) =>
  fetchJSON("/league/affiliations", {
    method: "POST",
    body: JSON.stringify({ tag }),
  });

// One route, two meanings, chosen server-side by the row's status: a pending
// row is cancelled outright with no guard; an active one is detached and 409s
// teamHasScheduledGames while any non-completed game references it. The team
// itself always survives.
const deleteLeagueAffiliation = (teamId) =>
  fetchJSON(`/league/affiliations/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
  });

// Tournaments (single elimination, 2026-08) — the detail route reconciles
// the bracket server-side on every GET/PUT (advances winners from completed
// LeagueGames, mints the next round's game once both its teams are known),
// so callers just re-fetch after any mutation rather than computing
// advancement locally.
const getLeagueTournaments = () => fetchJSON("/league/tournaments");

const createLeagueTournament = (payload) =>
  fetchJSON("/league/tournaments", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const getLeagueTournament = (tournamentId) =>
  fetchJSON(`/league/tournaments/${encodeURIComponent(tournamentId)}`);

// `payload` is multiplexed, same shape the Lambda accepts: any of
// { seed: { mode: "random" } }, { seed: { mode: "manual", positions: [...] } },
// { slotWinner: { slotKey, winnerTeamId } }, and/or { name?, startDate?,
// startTime?, location? }.
const updateLeagueTournament = (tournamentId, payload) =>
  fetchJSON(`/league/tournaments/${encodeURIComponent(tournamentId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const deleteLeagueTournament = (tournamentId) =>
  fetchJSON(`/league/tournaments/${encodeURIComponent(tournamentId)}`, {
    method: "DELETE",
  });

// Public claim-link flow (polyp-staff-invite-claim, no auth) — the invitee
// looks up the invite from the {inviteId}.{secret} token in the /join/:token
// URL, then submits their own email + password. On success the response is
// { customToken, uid, accountType } — the caller signs in with
// signInWithCustomToken, same as ApiService.login.
const lookupStaffInvite = (token) =>
  publicFetchJSON(`/public/staff-invites/lookup`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });

const claimStaffInvite = ({ token, email, password, firstName, lastName }) =>
  publicFetchJSON(`/public/staff-invites/claim`, {
    method: "POST",
    body: JSON.stringify({ token, email, password, firstName, lastName }),
  });

// Platform-admin: per-game official assignment console (referee + stat keeper
// slots, applicant pools, transactional accept with the cross-role guard).
const getAdminAssignments = () => fetchJSON("/admin/assignments");

const getAdminAssignmentDetail = (matchId) =>
  fetchJSON(`/admin/assignments/${encodeURIComponent(matchId)}`);

const acceptOfficialAssignment = (matchId, officialId, role) =>
  fetchJSON(`/admin/assignments/${encodeURIComponent(matchId)}/accept`, {
    method: "POST",
    body: JSON.stringify({ officialId, role }),
  });

const unassignOfficialRole = (matchId, role) =>
  fetchJSON(`/admin/assignments/${encodeURIComponent(matchId)}/unassign`, {
    method: "POST",
    body: JSON.stringify({ role }),
  });

// Platform-admin: game-official account approval queue. Omit status to list all.
const getAdminOfficials = (status) =>
  fetchJSON(`/admin/officials${status ? `?status=${encodeURIComponent(status)}` : ""}`);

const setAdminOfficialStatus = (userId, officialStatus) =>
  fetchJSON(`/admin/officials/${encodeURIComponent(userId)}/status`, {
    method: "POST",
    body: JSON.stringify({ officialStatus }),
  });

// Guardian-facing: the "Matches" hub (profiles + upcoming) and a child's game stats.
const getMyMatches = () => fetchJSON("/me/matches");

const getMyChildStats = (childId) =>
  fetchJSON(`/me/matches/${encodeURIComponent(childId)}/stats`);

// REMOVED-FOR-WEB-ONLY-STRIPE-ADD-CLIENTS (2026-05-20) — CSV import creates clients
// without Stripe and is disabled along with the manual add path.
// const importClients = (businessId, data) =>
//   fetchJSON(`/businesses/${encodeURIComponent(businessId)}/clients/import`, {
//     method: "POST",
//     body: JSON.stringify(data),
//   });

const getEnrichedClient = (businessId, clientId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/clients/${encodeURIComponent(clientId)}`
  );

const updateClient = (businessId, clientId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/clients/${encodeURIComponent(clientId)}`,
    { method: "PUT", body: JSON.stringify(payload) }
  );

const deleteClient = (businessId, clientId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/clients/${encodeURIComponent(clientId)}`,
    { method: "DELETE" }
  );

// --- Custom Fields ---
// Single source of truth: Businesses.customFields. Same endpoint iOS uses
// (PUT /businesses/{id}/custom-fields). Schema: [{id, name, type, options?, sortOrder}].
const updateBusinessCustomFields = (businessId, customFields) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/custom-fields`, {
    method: "PUT",
    body: JSON.stringify({ customFields }),
  });

// --- Locations ---
// Stored on Businesses.locations as [{id, name, address?}]. Server fills
// missing ids and cascades renames/deletes to BusinessToClients.location.
const updateBusinessLocations = (businessId, locations) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/locations`, {
    method: "PUT",
    body: JSON.stringify({ locations }),
  });

// --- Create Business (multi-step onboarding) ---
const verifyCreateBusinessPassword = (password) =>
  fetchJSON("/verify-create-password", {
    method: "POST",
    body: JSON.stringify({ password }),
  });

const createBusiness = (payload) =>
  fetchJSON("/businesses", {
    method: "POST",
    body: JSON.stringify(payload),
  });

const getBusinessImageUploadUrl = (businessId, fileType) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/images/URL`, {
    method: "POST",
    body: JSON.stringify({ businessId, fileType }),
  });

// --- Business Image Upload ---
const uploadBusinessImage = async (businessId, file, mimeType) => {
  if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE_URL environment variable");

  const url = `${API_BASE}/businesses/${encodeURIComponent(businessId)}/image`;

  const headers = {};
  try {
    if (AuthService?.getAccessToken) {
      const token = await AuthService.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
  } catch (_) {
    // token retrieval failed
  }

  if (API_KEY) headers["x-api-key"] = API_KEY;

  const formData = new FormData();
  formData.append("file", file);
  if (mimeType) formData.append("mimeType", mimeType);

  const res = await fetch(url, { method: "POST", headers, body: formData });

  if (!res.ok) {
    throw new Error(`uploadBusinessImage failed: ${res.status} ${res.statusText}`);
  }

  let json = {};
  try {
    json = await res.json();
  } catch {}
  return json;
};

// --- Update Business ---
const updateBusiness = async (businessId, updates) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

// --- Grant authorization to another user (mirrors iOS GrantAuthorizationViewModel) ---
// authorizationType: "Read Only" | "Read and Write" | "Everything"
const grantBusinessAuthorization = (businessId, targetUserId, authorizationType) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/authorized-users`, {
    method: "POST",
    body: JSON.stringify({ targetUserId, authorizationType }),
  });

// --- Stripe onboarding (mirrors iOS completeVerification) ---
// Returns { onboardingUrl } for the host to complete identity verification.
const startStripeOnboarding = (businessId) =>
  fetchJSON(`/payments/onboarding`, {
    method: "POST",
    body: JSON.stringify({ businessId }),
  });

// Live Stripe account status — hits Stripe API + falls back to cached DB values.
// Returns { hasStripeAccount, chargesEnabled, payoutsEnabled, onboardingComplete,
// requiresAction, currentlyDue[], ... }. Used by the /onboarding/complete return page.
const getStripeAccountStatus = (businessId) =>
  fetchJSON(`/payments/account-status?businessId=${encodeURIComponent(businessId)}`);

// ===============================================
// NEW: BUSINESS OPS + SHIPPING HELPERS (internal)
// NOTE: These endpoints must exist on the backend:
// - GET  /internal/pending-clients/{businessId}
// - PATCH /internal/pending-clients/{businessId}/{personId}
// - GET  /internal/shipments/{businessId}
// ===============================================

const getInternalPendingClients = (businessId) =>
  fetchJSON(`/internal/pending-clients/${encodeURIComponent(businessId)}`);

const patchInternalPendingClient = (businessId, personId, payload) =>
  fetchJSON(
    `/internal/pending-clients/${encodeURIComponent(businessId)}/${encodeURIComponent(personId)}`,
    { method: "PATCH", body: JSON.stringify(payload) }
  );

const getInternalShipments = (businessId) =>
  fetchJSON(`/internal/shipments/${encodeURIComponent(businessId)}`);

const createShippingLabel = (payload) =>
  fetchJSON(`/internal/shipments/create`, { method: "POST", body: JSON.stringify(payload) });

const getShipmentStatus = (transactionId) =>
  fetchJSON(`/internal/shipments/status/${encodeURIComponent(transactionId)}`);

// ===============================================
// PLANS
// ===============================================

const getBusinessPlans = (businessId) =>
  fetchJSON(`/payments/plans?businessId=${encodeURIComponent(businessId)}`);

const createBusinessPlan = (businessId, planData) =>
  fetchJSON(`/payments/plans`, {
    method: "POST",
    body: JSON.stringify({ businessId, ...planData }),
  });

const updateBusinessPlan = (businessId, planId, updates) =>
  fetchJSON(`/payments/plans/${encodeURIComponent(planId)}`, {
    method: "PUT",
    body: JSON.stringify({ businessId, ...updates }),
  });

const deleteBusinessPlan = (businessId, planId) =>
  fetchJSON(`/payments/plans/${encodeURIComponent(planId)}?businessId=${encodeURIComponent(businessId)}`, {
    method: "DELETE",
  });

// ===============================================
// CRM ENDPOINTS
// ===============================================

const getCRMDashboard = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/crm/dashboard`);

const getCRMRevenue = (businessId, year, month) => {
  const params = new URLSearchParams();
  if (year) params.set("year", year);
  if (month) params.set("month", String(month).padStart(2, "0"));
  return fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/revenue?${params.toString()}`
  );
};

const getCRMHealth = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/crm/health`);

const activateClient = (businessId, personId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/crm/activate`, {
    method: "POST",
    body: JSON.stringify({ personId }),
  });

const updatePaymentStatus = (businessId, clientId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/payments/${encodeURIComponent(clientId)}`,
    { method: "PUT", body: JSON.stringify(payload) }
  );

const pushPayment = (businessId, clientId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/payments/${encodeURIComponent(clientId)}/push`,
    { method: "POST", body: JSON.stringify(payload) }
  );

const pauseSubscription = (businessId, clientId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/subscriptions/${encodeURIComponent(clientId)}/pause`,
    { method: "POST", body: JSON.stringify(payload || {}) }
  );

const resumeSubscription = (businessId, clientId, payload) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/subscriptions/${encodeURIComponent(clientId)}/resume`,
    { method: "POST", body: JSON.stringify(payload || {}) }
  );

const cancelSubscription = (businessId, clientId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/subscriptions/${encodeURIComponent(clientId)}/cancel`,
    { method: "POST" }
  );

const retryPayment = (businessId, clientId) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/payments/${encodeURIComponent(clientId)}/retry`,
    { method: "POST" }
  );

const updateClientAmount = (businessId, clientId, amount) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/clients/${encodeURIComponent(clientId)}/amount`,
    { method: "PUT", body: JSON.stringify({ amount }) }
  );

const resetYearHigh = (businessId, yearHighOverride) =>
  fetchJSON(
    `/businesses/${encodeURIComponent(businessId)}/crm/revenue/reset-year-high`,
    { method: "POST", body: JSON.stringify({ yearHighOverride }) }
  );

// ===============================================
// SCHEDULING PRIMITIVES
// ===============================================

// --- Availability ---
const getAvailability = (businessId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/availability${qs ? `?${qs}` : ''}`);
};
const createAvailability = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/availability`, { method: "POST", body: JSON.stringify(payload) });
const updateAvailability = (businessId, availabilityId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/availability/${encodeURIComponent(availabilityId)}`, { method: "PUT", body: JSON.stringify(payload) });
const deleteAvailability = (businessId, availabilityId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/availability/${encodeURIComponent(availabilityId)}`, { method: "DELETE" });

// --- Sessions ---
const getSessions = (businessId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/sessions${qs ? `?${qs}` : ''}`);
};
const createSession = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/sessions`, { method: "POST", body: JSON.stringify(payload) });
const updateSession = (businessId, sessionId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/sessions/${encodeURIComponent(sessionId)}`, { method: "PUT", body: JSON.stringify(payload) });

// --- Check-ins ---
const getCheckIns = (businessId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  const sessionId = params.sessionId;
  if (sessionId) {
    return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/checkins/${encodeURIComponent(sessionId)}${qs ? `?${qs}` : ''}`);
  }
  return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/checkins${qs ? `?${qs}` : ''}`);
};
const createCheckIn = (businessId, sessionId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/checkins/${encodeURIComponent(sessionId)}`, { method: "POST", body: JSON.stringify(payload) });

// --- Resources ---
const getResources = (businessId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/resources${qs ? `?${qs}` : ''}`);
};
const createResource = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/resources`, { method: "POST", body: JSON.stringify(payload) });
const updateResource = (businessId, resourceId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/resources/${encodeURIComponent(resourceId)}`, { method: "PUT", body: JSON.stringify(payload) });
const deleteResource = (businessId, resourceId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/resources/${encodeURIComponent(resourceId)}`, { method: "DELETE" });

// --- Progressions ---
const getProgressions = (businessId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions${qs ? `?${qs}` : ''}`);
};
const createProgression = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions`, { method: "POST", body: JSON.stringify(payload) });
const updateProgression = (businessId, progressionId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions/${encodeURIComponent(progressionId)}`, { method: "PUT", body: JSON.stringify(payload) });
const deleteProgression = (businessId, progressionId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions/${encodeURIComponent(progressionId)}`, { method: "DELETE" });

// --- Enrollments ---
const getEnrollments = (businessId, progressionId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions/${encodeURIComponent(progressionId)}/enrollments`);
const enrollClients = (businessId, progressionId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/progressions/${encodeURIComponent(progressionId)}/enrollments`, { method: "POST", body: JSON.stringify(payload) });

// ===============================================
// CLIENT MODE — endpoints used by the /app client portal.
// All hit existing iOS Lambdas; no new backend code.
// ===============================================

// /me
const getMyChildren = () => fetchJSON("/me/children");
const deleteMe = () => fetchJSON("/me", { method: "DELETE" });

// Both fields are independent at the Lambda. If you want to update only one,
// pass `undefined` for the other and it will be omitted from the JSON body
// (Lambda treats absent keys as "leave alone"; null clears).
const updateMyInitials = (initials, profileColorId) => {
  const body = {};
  if (initials !== undefined) body.initials = initials ?? null;
  if (profileColorId !== undefined) body.profileColorId = profileColorId ?? null;
  return fetchJSON("/me/initials", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
};

const updateMyChildInitials = (childId, initials, profileColorId) => {
  const body = {};
  if (initials !== undefined) body.initials = initials ?? null;
  if (profileColorId !== undefined) body.profileColorId = profileColorId ?? null;
  return fetchJSON(`/me/children/${encodeURIComponent(childId)}/initials`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
};

const uploadProfileImage = async (file) => {
  if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE_URL environment variable");
  const headers = {};
  try {
    if (AuthService?.getAccessToken) {
      const token = await AuthService.getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
  } catch (_) {}
  if (API_KEY) headers["x-api-key"] = API_KEY;

  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE}/me/profile/image`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error(`uploadProfileImage failed: ${res.status} ${res.statusText}`);
  try {
    return await res.json();
  } catch {
    return {};
  }
};

// Schedule
const getMyTodaysSessions = (days = 14) =>
  fetchJSON(`/me/schedule/today?days=${encodeURIComponent(days)}`);

const getMyBusinessSchedule = (businessId, from, days = 7) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  qs.set("days", String(days));
  return fetchJSON(`/me/businesses/${encodeURIComponent(businessId)}/schedule?${qs.toString()}`);
};

const getMyBusinessProgressions = (businessId) =>
  fetchJSON(`/me/businesses/${encodeURIComponent(businessId)}/progressions`);

const getMyAttendanceStats = (businessId, profileId) => {
  const qs = profileId ? `?profileId=${encodeURIComponent(profileId)}` : "";
  return fetchJSON(`/me/businesses/${encodeURIComponent(businessId)}/attendance-stats${qs}`);
};

// Subscriptions (read-only v1: cancel + reactivate are the only writes)
const cancelMySubscription = (payload) =>
  fetchJSON(`/payments/subscriptions/cancel`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });

const reactivateMySubscription = (payload) =>
  fetchJSON(`/payments/subscriptions/reactivate`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });

// Businesses & reviews
const searchBusinesses = (query) => {
  const qs = query ? `?q=${encodeURIComponent(query)}` : "";
  return fetchJSON(`/businesses${qs}`);
};

const getBusinessReviews = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/reviews`);

const submitBusinessReview = (businessId, payload) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/reviews`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

const deleteBusinessReview = (businessId) =>
  fetchJSON(`/businesses/${encodeURIComponent(businessId)}/reviews`, {
    method: "DELETE",
  });

// Groups (client side)
const getMyGroups = (profileId) => {
  const qs = profileId ? `?profileId=${encodeURIComponent(profileId)}` : "";
  return fetchJSON(`/me/subscriptions/groups${qs}`);
};

const joinGroup = (payload) =>
  fetchJSON(`/me/subscriptions/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

// DMs (client side)
const listMyConversations = (profileId) => {
  const qs = profileId ? `?profileId=${encodeURIComponent(profileId)}` : "";
  return fetchJSON(`/me/dm/conversations${qs}`);
};

// Announcements + read receipts
const getMyAnnouncements = () => fetchJSON(`/me/announcements`);

const markAsRead = (payload) =>
  fetchJSON(`/me/read-receipts`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });

// Auth: SMS verification + shadow account claim
// `lookupOnly` (shadow mode only): probe shadow existence without sending SMS.
// `mode`: "shadow" (default, shadow-claim flow) or "new" (brand-new signup,
//   no shadow row — backend writes the OTP to the PhoneVerifications table).
const sendPhoneVerification = (phoneNumber, { lookupOnly = false, mode } = {}) =>
  publicFetchJSON(`/auth/send-verification`, {
    method: "POST",
    body: JSON.stringify({
      phoneNumber,
      ...(mode === "new" ? { mode: "new" } : {}),
      ...(lookupOnly ? { lookupOnly: true } : {}),
    }),
  });

// Unauthenticated phone-existence probe. Used by both clients BEFORE kicking
// off Firebase Phone Auth on a social signup so the UI can render the "this
// phone is already a Polyp account" block copy without sending an OTP first.
// Returns: { shadowFound, realUserExists?, shadowUserId?, maskedPhone? }
const lookupPhone = (phoneNumber) =>
  publicFetchJSON(`/auth/lookup`, {
    method: "POST",
    body: JSON.stringify({ phoneNumber }),
  });

// Validates the OTP without consuming the shadow row. Lambda extends
// codeExpiresAt to +30 min so the subsequent claim call can reuse the code.
const verifyShadowOtpOnly = (shadowUserId, code) =>
  publicFetchJSON(`/auth/verify-claim`, {
    method: "POST",
    body: JSON.stringify({ mode: "verify-only", shadowUserId, code }),
  });

// Validates an OTP from the PhoneVerifications table for a brand-new signup
// with no shadow row. On success the backend marks verified=true; the user
// then has 30 min to call POST /users to actually create their account.
const verifyNewPhone = (phoneNumber, code) =>
  publicFetchJSON(`/auth/verify-claim`, {
    method: "POST",
    body: JSON.stringify({ mode: "verify-new", phoneNumber, code }),
  });

// Email-or-phone sign-in. The Lambda resolves a phone identifier to the
// user's email server-side, verifies the password via Firebase Identity
// Toolkit, and returns a Firebase custom token. The client completes the
// session with signInWithCustomToken — the resolved email never leaves
// the server. Generic 401 on any failure (wrong password, unknown phone,
// shadow account) — no enumeration signal.
const login = ({ identifier, password }) =>
  publicFetchJSON(`/auth/login`, {
    method: "POST",
    body: JSON.stringify({ identifier, password }),
  });

// Run after Firebase signup to migrate the shadow row to the new Firebase UID.
// Authenticated by `firebaseUid` in the body, not the bearer token.
const claimShadow = ({ shadowUserId, code, firebaseUid, firstName, lastName, email, phoneNumber, dob }) =>
  publicFetchJSON(`/auth/verify-claim`, {
    method: "POST",
    body: JSON.stringify({
      mode: "claim",
      shadowUserId,
      code,
      firebaseUid,
      firstName,
      lastName,
      email,
      phoneNumber,
      dob,
    }),
  });

// ===============================================
// MAIN EXPORT
// ===============================================

const ApiService = {
  // Current user (/me)
  getMe,
  getMeProfile,
  getMeBusiness,
  getMySubscriptions,
  getAuthorizedBusinesses,

  // Public
  getPublicRegistrationLink,

  // Clients
  getClients,
  addClient,
  addManualClient,
  createPendingClient,
  collectClientPayment,
  requestSubscription,
  acceptSelfRequest,
  denySelfRequest,
  parentSetupIntent,
  lookupClientByPhone,
  // REMOVED-FOR-WEB-ONLY-STRIPE-ADD-CLIENTS (2026-05-20)
  // importClients,
  getEnrichedClient,

  // Matchmaking
  createMatchRequest,
  getMyMatchRequests,
  cancelMatchRequest,
  getBusinessMatches,
  getBusinessMatchStats,
  getAdminMatchRequests,
  createMatch,
  getAdminMatches,
  getAdminMatchStats,
  saveAdminMatchStats,
  getAdminPlacements,
  placeAdminLead,
  getMyMatches,
  getMyChildStats,

  // Game officials
  getOfficialGames,
  getOfficialMatchStats,
  saveOfficialMatchStats,

  // Coach tier
  getCoachTeams,
  createCoachTeam,
  getCoachAffiliations,
  acceptCoachAffiliation,
  declineCoachAffiliation,
  getCoachTeamLogoUploadUrl,
  updateCoachTeam,
  getCoachPlayers,
  createCoachPlayer,
  updateCoachPlayer,
  archiveCoachPlayer,
  getCoachGames,
  getCoachGameStats,
  getCoachPlayerStats,

  // League tier
  getLeague,
  putLeague,
  getLeagueTeams,
  createLeagueTeam,
  updateLeagueTeam,
  deleteLeagueTeam,
  getLeagueTeamLogoUploadUrl,
  getLeagueTeamPlayers,
  createLeagueTeamPlayer,
  updateLeagueTeamPlayer,
  archiveLeagueTeamPlayer,
  getLeagueGames,
  createLeagueGame,
  updateLeagueGame,
  deleteLeagueGame,
  getLeagueOfficials,
  removeLeagueOfficial,
  createLeagueInvite,
  getLeagueInvites,
  resendLeagueInvite,
  revokeLeagueInvite,
  getLeagueAffiliations,
  createLeagueAffiliation,
  deleteLeagueAffiliation,
  getLeagueTournaments,
  createLeagueTournament,
  getLeagueTournament,
  updateLeagueTournament,
  deleteLeagueTournament,
  lookupStaffInvite,
  claimStaffInvite,

  getAdminOfficials,
  setAdminOfficialStatus,
  getAdminAssignments,
  getAdminAssignmentDetail,
  acceptOfficialAssignment,
  unassignOfficialRole,

  updateClient,
  deleteClient,

  // Field Definitions
  updateBusinessCustomFields,
  updateBusinessLocations,

  // Business assets
  uploadBusinessImage,
  updateBusiness,
  grantBusinessAuthorization,
  startStripeOnboarding,
  getStripeAccountStatus,

  // Create Business
  verifyCreateBusinessPassword,
  createBusiness,
  getBusinessImageUploadUrl,

  // Business ops + Shipping
  getInternalPendingClients,
  patchInternalPendingClient,
  getInternalShipments,
  createShippingLabel,
  getShipmentStatus,

  // Plans
  getBusinessPlans,
  createBusinessPlan,
  updateBusinessPlan,
  deleteBusinessPlan,

  // CRM
  getCRMDashboard,
  getCRMRevenue,
  getCRMHealth,
  activateClient,
  updatePaymentStatus,
  pushPayment,
  pauseSubscription,
  resumeSubscription,
  cancelSubscription,
  retryPayment,
  updateClientAmount,
  resetYearHigh,

  // Scheduling Primitives
  getAvailability,
  createAvailability,
  updateAvailability,
  deleteAvailability,
  getSessions,
  createSession,
  updateSession,
  getCheckIns,
  createCheckIn,
  getResources,
  createResource,
  updateResource,
  deleteResource,
  getProgressions,
  createProgression,
  updateProgression,
  deleteProgression,
  getEnrollments,
  enrollClients,

  // Groups
  getGroups,
  getGroupMembers,
  createGroup,
  addClientsToGroup,
  updateGroup,
  deleteGroup,
  removeClientsFromGroup,

  // Direct Messages
  listBusinessConversations: (businessId) =>
    fetchJSON(`/businesses/${encodeURIComponent(businessId)}/dm/conversations`),
  createConversation: (businessId, payload) =>
    fetchJSON(`/businesses/${encodeURIComponent(businessId)}/dm/conversations`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  getConversationMessages: (conversationId, limit = 50) =>
    fetchJSON(`/dm/conversations/${encodeURIComponent(conversationId)}/messages?limit=${limit}`),
  sendDirectMessage: (conversationId, payload) =>
    fetchJSON(`/dm/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: "POST", body: JSON.stringify(payload),
    }),
  batchSendDM: (businessId, payload) =>
    fetchJSON(`/businesses/${encodeURIComponent(businessId)}/dm/batch`, {
      method: "POST", body: JSON.stringify(payload),
    }),

  // Group Messages (Announcements)
  getGroupMessages: (businessId, groupId, limit = 50) =>
    fetchJSON(`/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}/messages?limit=${limit}`),
  sendGroupMessage: (businessId, groupId, payload) =>
    fetchJSON(`/businesses/${encodeURIComponent(businessId)}/groups/${encodeURIComponent(groupId)}/messages`, {
      method: "POST", body: JSON.stringify(payload),
    }),

  // ===== CLIENT MODE =====
  // /me
  getMyChildren,
  deleteMe,
  uploadProfileImage,
  updateMyInitials,
  updateMyChildInitials,
  // Schedule
  getMyTodaysSessions,
  getMyBusinessSchedule,
  getMyBusinessProgressions,
  getMyAttendanceStats,
  // Subscriptions (client writes)
  cancelMySubscription,
  reactivateMySubscription,
  // Businesses & reviews
  searchBusinesses,
  getBusinessReviews,
  submitBusinessReview,
  deleteBusinessReview,
  // Groups + DMs (client side)
  getMyGroups,
  joinGroup,
  listMyConversations,
  // Announcements + read receipts
  getMyAnnouncements,
  markAsRead,
  // Phone auth
  sendPhoneVerification,
  lookupPhone,
  verifyShadowOtpOnly,
  verifyNewPhone,
  claimShadow,
  login,

  // Account notifications (business invites)
  getMyAccountNotifications: () => fetchJSON('/me/business-invites'),
  respondToBusinessInvite: (inviteId, action, linkToExistingChildId = null) =>
    fetchJSON(`/me/business-invites/${encodeURIComponent(inviteId)}/${action}`, {
      method: 'POST',
      body: JSON.stringify(linkToExistingChildId ? { linkToExistingChildId } : {}),
    }),
};

export default ApiService;
